import { useCarePage, type CarePresentation } from "@/lib/api/care";
import axios from "axios";
import { PostHogEvents, trackEvent } from "@/lib/posthog-events";
import { Skeleton } from "@somethings-ui/web";
import { useState } from "react";
import { AboutThisRecord } from "./about-this-record";
import { CareHistory } from "./care-history";
import { CareSnapshot } from "./care-snapshot";
import { CareUpdateSection } from "./care-update-section";
import { FindPresentationDialog } from "./find-presentation-dialog";
import { PresentationReviewCard } from "./presentation-review-card";
import { PrimaryPresentation } from "./primary-presentation";
import { WriteCareUpdateDialog } from "./write-care-update-dialog";

type Flow =
  | { kind: "closed" }
  | { kind: "find" }
  | { kind: "update"; presentation?: CarePresentation };

const carePageError = (error: unknown) =>
  axios.isAxiosError(error) && error.response?.data?.reason === "REMATCHED"
    ? "This teen has rematched and is now under a new supervisor."
    : "Unable to load this care page.";

export function CareTab({ menteeId }: { menteeId: string }) {
  const { data: page, isPending, isError, error } = useCarePage(menteeId);
  const [flow, setFlow] = useState<Flow>({ kind: "closed" });
  const close = () => setFlow({ kind: "closed" });

  if (isPending) return <CareTabSkeleton />;
  if (isError || !page) {
    return (
      <p className="font-body text-sm text-states-error">
        {carePageError(error)}
      </p>
    );
  }

  return (
    <div className="grid grid-cols-[minmax(0,2fr)_minmax(0,1fr)] gap-6">
      <div className="flex flex-col gap-6">
        <PrimaryPresentation
          page={page}
          onChangePresentation={() => {
            trackEvent(PostHogEvents.CARE_PAGE_CHANGE_PRESENTATION_CLICKED, {
              mentee_id: menteeId,
            });
            setFlow({ kind: "find" });
          }}
        />
        {page.care_reviews_enabled !== false && page.open_review ? (
          <PresentationReviewCard menteeId={menteeId} page={page} />
        ) : null}
        {page.care_reviews_enabled !== false ? (
          <CareUpdateSection
            update={page.care_update}
            onWrite={() => {
              trackEvent(PostHogEvents.CARE_PAGE_WRITE_CARE_UPDATE_CLICKED, {
                mentee_id: menteeId,
              });
              setFlow({ kind: "update" });
            }}
          />
        ) : null}
        <CareHistory
          menteeId={menteeId}
          billing={page.care_reviews_enabled !== false}
        />
      </div>
      <div className="flex flex-col gap-6">
        <CareSnapshot menteeId={menteeId} />
        <AboutThisRecord menteeId={menteeId} advisor={page.clinical_advisor} />
      </div>

      <FindPresentationDialog
        open={flow.kind === "find"}
        current={page.presentation}
        onClose={close}
        onSelect={(presentation) => setFlow({ kind: "update", presentation })}
      />
      <WriteCareUpdateDialog
        key={
          flow.kind === "update"
            ? flow.presentation?.code ?? "update"
            : "closed"
        }
        menteeId={menteeId}
        open={flow.kind === "update"}
        presentation={flow.kind === "update" ? flow.presentation : undefined}
        onClose={close}
      />
    </div>
  );
}

function CareTabSkeleton() {
  return (
    <div
      className="grid grid-cols-[minmax(0,2fr)_minmax(0,1fr)] gap-6"
      aria-busy="true"
    >
      <div className="flex flex-col gap-6">
        <Skeleton className="h-56 rounded-[20px]" />
        <Skeleton className="h-40 rounded-[20px]" />
        <Skeleton className="h-80 rounded-[20px]" />
      </div>
      <div className="flex flex-col gap-6">
        <Skeleton className="h-[480px] rounded-[20px]" />
        <Skeleton className="h-56 rounded-[20px]" />
      </div>
    </div>
  );
}
